import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCrypto } from '../contexts/CryptoContext';
import { checkinService } from '../services/checkinService';
import { supabase } from '../services/supabaseClient';
import { getAll as getBeneficiaries } from '../services/beneficiaryService';
import { getTransmissionOverview, resyncEnvelopes } from '../services/transmissionService';
import { sendCheckinAlert } from '../services/emailService';
import GlowCard, { GlowIcon } from '../components/GlowCard';
import OnboardingChecklist from '../components/OnboardingChecklist';
import TransmissionRecap from '../components/TransmissionRecap';
import TransmissionWarnings from '../components/TransmissionWarnings';

const S = '#00F5A0';
const G = '#8892A4';

const CARD = {
  background: 'rgba(255,255,255,0.04)',
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
  border: '1px solid rgba(255,255,255,0.07)',
  borderRadius: 16,
  padding: '20px',
};

const LockIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="7" width="14" height="10" rx="2"/>
    <path d="M5 7V5a4 4 0 118 0v2"/>
    <circle cx="9" cy="12" r="1.5"/>
  </svg>
);

const PeopleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="6.5" cy="5.5" r="2.5"/>
    <path d="M1 16v-1.5A3.5 3.5 0 014.5 11h4A3.5 3.5 0 0112 14.5V16"/>
    <circle cx="13" cy="6" r="2"/>
    <path d="M14 11a3 3 0 012.5 3.5V16"/>
  </svg>
);

const IS_DEMO = process.env.REACT_APP_DEMO_MODE === 'true';

// PF-03 CP2 (D3) : resynchronisation des enveloppes une seule fois par
// déverrouillage. Chaque déverrouillage produit une nouvelle CryptoKey MK :
// un revisit du Dashboard ne relance rien, un nouveau déverrouillage si.
const resyncedMasterKeys = new WeakSet();

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { masterKey } = useCrypto();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alertDismissed, setAlertDismissed] = useState(false);
  const [testAlertLoading, setTestAlertLoading] = useState(false);
  const [testAlertResult, setTestAlertResult] = useState(null);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [beneficiariesLoaded, setBeneficiariesLoaded] = useState(false);
  const [resyncFailed, setResyncFailed] = useState(false);
  const [transmissionOverview, setTransmissionOverview] = useState(null);

  const loadStats = useCallback(async () => {
    setLoading(true);
    try {
      const [vaultResult, beneCountResult, checkinResult] = await Promise.all([
        supabase.from('vault_items').select('category'),
        supabase.from('beneficiaries').select('id', { count: 'exact', head: true }).eq('is_active', true).neq('status', 'revoked'),
        checkinService.getConfig(),
      ]);

      const vaultItems = vaultResult.data || [];
      const vaultByCategory = {};
      vaultItems.forEach(item => {
        vaultByCategory[item.category] = (vaultByCategory[item.category] || 0) + 1;
      });

      const cfg = checkinResult.data;
      const hasEverCheckedIn = !!cfg?.last_check_in;
      const lastCheckinDate  = cfg?.last_check_in      || null;
      const nextCheckinDate  = cfg?.next_check_in_due  || null;
      let daysUntilNextCheckin = null;
      let isCheckinOverdue = false;
      if (hasEverCheckedIn && nextCheckinDate) {
        const diffMs = new Date(nextCheckinDate) - Date.now();
        daysUntilNextCheckin = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
        isCheckinOverdue = daysUntilNextCheckin <= 0;
      }

      setStats({
        total_vault_items:       vaultItems.length,
        total_beneficiaries:     beneCountResult.count ?? 0,
        days_until_next_checkin: daysUntilNextCheckin,
        has_ever_checked_in:     hasEverCheckedIn,
        last_checkin_date:       lastCheckinDate,
        is_checkin_overdue:      isCheckinOverdue,
        next_checkin_date:       nextCheckinDate,
        is_premium:              user?.is_premium || false,
        vault_by_category:       vaultByCategory,
      });
    } catch (e) {
      console.error('[SafeHeir] Dashboard loadStats:', e);
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    loadStats();
  }, [user, navigate, loadStats]);

  useEffect(() => {
    if (!masterKey) {
      setBeneficiaries([]);
      setBeneficiariesLoaded(true);
      return;
    }
    (async () => {
      try {
        const result = await getBeneficiaries(masterKey);
        setBeneficiaries(result.data || []);
        setBeneficiariesLoaded(true);
        if (result.success && !resyncedMasterKeys.has(masterKey)) {
          resyncedMasterKeys.add(masterKey);
          const report = await resyncEnvelopes({ beneficiaries: result.data, masterKey });
          setResyncFailed(report.errors.length > 0);
        }
        // PF-03 CP3.1 : avertissements recalculés à chaque montage, après l'éventuel resync.
        if (result.success) {
          const overview = await getTransmissionOverview({ beneficiaries: result.data });
          setTransmissionOverview(overview.error ? null : overview);
        }
      } catch (err) {
        console.error('[SafeHeir] Dashboard getBeneficiaries:', err);
        setBeneficiariesLoaded(true);
      }
    })();
  }, [masterKey]);

  const handleTestAlert = async () => {
    if (!IS_DEMO) return;
    setTestAlertLoading(true);
    setTestAlertResult(null);
    try {
      const [benefResult, configResult] = await Promise.all([
        getBeneficiaries(masterKey),
        checkinService.getConfig(),
      ]);
      const beneficiaries = benefResult.data || [];
      const config = configResult.data || {};
      const sent = await sendCheckinAlert(beneficiaries, config);
      setTestAlertResult({ ok: true, count: sent, total: beneficiaries.length });
    } catch {
      setTestAlertResult({ ok: false });
    } finally {
      setTestAlertLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (loading || !stats) {
    return (
      <div style={{ minHeight: '100vh', background: '#0A1628', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 40, height: 40, border: '3px solid rgba(255,255,255,0.1)', borderTopColor: S, borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
          <p style={{ color: G, fontSize: 14 }}>Chargement...</p>
        </div>
      </div>
    );
  }

  const {
    total_vault_items,
    total_beneficiaries,
    days_until_next_checkin,
    has_ever_checked_in,
    last_checkin_date,
    is_checkin_overdue,
    next_checkin_date,
    is_premium,
    vault_by_category,
  } = stats;

  const showOverdueAlert = is_checkin_overdue && !alertDismissed;
  const firstName = user?.full_name?.split(' ')[0] || user?.email?.split('@')[0] || 'vous';

  const checkinValue = (() => {
    if (!has_ever_checked_in) return { label: 'Non programmé', color: G };
    if (is_checkin_overdue) return { label: `Retard de ${Math.abs(days_until_next_checkin ?? 0)} j`, color: '#f87171' };
    if (!Number.isFinite(days_until_next_checkin)) return { label: 'À définir', color: G };
    if (days_until_next_checkin <= 7) return { label: `Dans ${days_until_next_checkin} j`, color: '#fbbf24' };
    return { label: `Dans ${days_until_next_checkin} j`, color: S };
  })();

  const checkinBorder = is_checkin_overdue
    ? 'rgba(239,68,68,0.4)'
    : (has_ever_checked_in && Number.isFinite(days_until_next_checkin) && days_until_next_checkin <= 7)
      ? 'rgba(251,191,36,0.3)'
      : has_ever_checked_in
        ? `rgba(0,245,160,0.2)`
        : 'rgba(255,255,255,0.07)';

  const categoryLabels = {
    social: 'Réseaux sociaux', email: 'Emails', banking: 'Banques',
    shopping: 'Shopping', streaming: 'Streaming', crypto: 'Crypto', other: 'Autres',
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0A1628', color: '#fff', paddingTop: 80 }}>

      {/* ── Overdue alert ── */}
      {showOverdueAlert && (
        <div style={{ background: 'rgba(239,68,68,0.08)', borderBottom: '1px solid rgba(239,68,68,0.25)' }}>
          <div style={{ maxWidth: 1024, margin: '0 auto', padding: '12px 40px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
            <div>
              <span style={{ color: '#fca5a5', fontWeight: 600, fontSize: 14 }}>Check-in en retard — </span>
              <span style={{ color: '#fca5a5', fontSize: 14 }}>vos bénéficiaires pourraient être alertés bientôt.</span>
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <button onClick={() => navigate('/checkin')}
                style={{ background: '#ef4444', color: '#fff', border: 'none', borderRadius: 7, padding: '7px 16px', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>
                Confirmer ma présence
              </button>
              <button onClick={() => setAlertDismissed(true)}
                style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: 18, padding: '4px 8px' }}>
                ×
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ maxWidth: 1024, margin: '0 auto', padding: '32px 40px' }}>

        {/* ── Resync transmissions (PF-03 CP2, D3) ── */}
        {resyncFailed && (
          <div role="status" style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 8, padding: '10px 14px', marginBottom: 20, color: '#F59E0B', fontSize: 13 }}>
            La mise à jour de vos transmissions préparées n'a pas abouti. Elle sera retentée au prochain déverrouillage du coffre.
          </div>
        )}

        {/* ── Header ── */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 32, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1 style={{ fontSize: 26, fontWeight: 700, margin: 0 }}>Bonjour, {firstName}</h1>
            <p style={{ margin: '4px 0 0', color: G, fontSize: 14 }}>
              {last_checkin_date
                ? `Dernier check-in le ${new Date(last_checkin_date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}`
                : "Aucun check-in effectué pour l'instant"}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <GlowCard variant="sidebar-item" onClick={() => navigate('/settings')} style={{ width: 'auto' }}>
              <span style={{ color: G, fontSize: 13, fontWeight: 500 }}>Paramètres</span>
            </GlowCard>
            <GlowCard variant="sidebar-item" onClick={handleLogout} style={{ width: 'auto' }}>
              <span style={{ color: 'rgba(255,255,255,0.35)', fontSize: 13 }}>Déconnexion</span>
            </GlowCard>
          </div>
        </div>

        {/* ── Onboarding checklist ── */}
        <OnboardingChecklist stats={stats} userId={user?.id} />

        {/* ── 4 Stat cards ── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 28 }}>

          <StatCard
            icon={<LockIcon />}
            label="Coffre-fort"
            value={total_vault_items}
            sub={total_vault_items === 0 ? 'Aucun élément' : `élément${total_vault_items > 1 ? 's' : ''} sécurisé${total_vault_items > 1 ? 's' : ''}`}
            onClick={() => navigate('/vault')}
            delay="0s"
          />

          <StatCard
            icon={<PeopleIcon />}
            label="Bénéficiaires"
            value={total_beneficiaries}
            sub={total_beneficiaries === 0 ? 'Aucun configuré' : `personne${total_beneficiaries > 1 ? 's' : ''} désignée${total_beneficiaries > 1 ? 's' : ''}`}
            onClick={() => navigate('/beneficiaries')}
            delay="0.2s"
          />

          {/* Check-in card */}
          <GlowCard float delay="0.4s" onClick={() => navigate('/checkin')} style={{
            border: `1px solid ${checkinBorder}`,
            cursor: 'pointer',
            background: is_checkin_overdue ? 'rgba(239,68,68,0.07)' : undefined,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <GlowIcon size={36} delay="0.4s">
                {is_checkin_overdue
                  ? <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#ef4444' }} />
                      <div style={{ position: 'absolute', width: 20, height: 20, borderRadius: '50%', background: 'rgba(239,68,68,0.3)', animation: 'ping 1.5s ease-out infinite' }} />
                    </div>
                  : <div style={{ width: 10, height: 10, borderRadius: '50%', background: checkinValue.color }} />
                }
              </GlowIcon>
              <span style={{ fontSize: 13, color: is_checkin_overdue ? '#fca5a5' : G, fontWeight: 500 }}>
                {is_checkin_overdue ? 'Check-in en retard !' : 'Prochain check-in'}
              </span>
            </div>
            <div style={{ fontSize: is_checkin_overdue ? 26 : 22, fontWeight: 800, color: checkinValue.color, marginBottom: 4 }}>
              {checkinValue.label}
            </div>
            {!has_ever_checked_in && <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)' }}>Effectuer un premier check-in →</div>}
            {has_ever_checked_in && !is_checkin_overdue && next_checkin_date && (
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)' }}>
                Avant le {new Date(next_checkin_date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
              </div>
            )}
            {is_checkin_overdue && (
              <div style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 6, background: '#ef4444', borderRadius: 6, padding: '5px 12px' }}>
                <span style={{ fontSize: 12, color: '#fff', fontWeight: 700 }}>Confirmer ma présence →</span>
              </div>
            )}
          </GlowCard>

          {/* Plan card */}
          <div onClick={() => !is_premium && navigate('/settings')}
            style={{ ...CARD, cursor: is_premium ? 'default' : 'pointer' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <div style={{ width: 36, height: 36, background: 'rgba(0,245,160,0.06)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.05em', color: is_premium ? S : 'rgba(0,245,160,0.5)' }}>{is_premium ? 'PRO' : 'FREE'}</span>
              </div>
              <span style={{ fontSize: 13, color: G, fontWeight: 500 }}>Mon plan</span>
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#fff', marginBottom: 4 }}>{is_premium ? 'Pro' : 'Gratuit'}</div>
            {!is_premium && <div style={{ fontSize: 12, color: S, fontWeight: 500 }}>Passer au plan Pro →</div>}
            {is_premium && <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)' }}>Accès complet activé</div>}
          </div>
        </div>

        {/* ── Quick actions ── */}
        <div style={{ ...CARD, marginBottom: 28 }}>
          <h2 style={{ fontSize: 12, fontWeight: 600, margin: '0 0 16px', color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
            Actions rapides
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
            <ActionButton label="Ajouter au coffre" sub="Sécuriser un compte" onClick={() => navigate('/vault')} />
            <ActionButton label="Ajouter un bénéficiaire" sub="Désigner une personne" onClick={() => navigate('/beneficiaries')} />
            <ActionButton
              label="Check-in de sécurité"
              sub={is_checkin_overdue ? 'En attente — agir maintenant' : 'Confirmer ma présence'}
              urgent={is_checkin_overdue}
              onClick={() => navigate('/checkin')}
            />
            <ActionButton label="Paramètres" sub="Profil et préférences" onClick={() => navigate('/settings')} />
          </div>

          {IS_DEMO && total_beneficiaries > 0 && (
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
              <button
                onClick={handleTestAlert}
                disabled={testAlertLoading}
                style={{
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(245,158,11,0.3)',
                  borderRadius: 10, padding: '14px',
                  cursor: testAlertLoading ? 'wait' : 'pointer',
                  textAlign: 'left', width: '100%',
                  transition: 'background 0.2s, border-color 0.2s',
                }}
                onMouseEnter={e => {
                  if (!testAlertLoading) {
                    e.currentTarget.style.background = 'rgba(245,158,11,0.06)';
                    e.currentTarget.style.borderColor = 'rgba(245,158,11,0.6)';
                  }
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.04)';
                  e.currentTarget.style.borderColor = 'rgba(245,158,11,0.3)';
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, fontSize: 14, color: '#F59E0B', marginBottom: 3 }}>
                  {testAlertLoading
                    ? <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <circle cx="8" cy="8" r="6" stroke="rgba(245,158,11,0.25)" strokeWidth="2"/>
                        <path d="M8 2a6 6 0 016 6" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    : <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M8 1.5A4.5 4.5 0 003.5 6v2.5L2.5 10h11l-1-1.5V6A4.5 4.5 0 008 1.5z" stroke="#F59E0B" strokeWidth="1.5" strokeLinejoin="round"/>
                        <path d="M6.5 10a1.5 1.5 0 003 0" stroke="#F59E0B" strokeWidth="1.5" strokeLinecap="round"/>
                      </svg>
                  }
                  {testAlertLoading ? 'Envoi en cours...' : "Tester l'alerte bénéficiaires"}
                </div>
                <div style={{ fontSize: 12, color: 'rgba(245,158,11,0.55)' }}>
                  Simule l'envoi automatique en cas d'absence prolongée
                </div>
              </button>

              {testAlertResult && (
                <div style={{
                  marginTop: 10, padding: '10px 14px', borderRadius: 8, fontSize: 13,
                  background: testAlertResult.ok ? 'rgba(0,245,160,0.08)' : 'rgba(239,68,68,0.08)',
                  border: `1px solid ${testAlertResult.ok ? 'rgba(0,245,160,0.25)' : 'rgba(239,68,68,0.25)'}`,
                  color: testAlertResult.ok ? '#00F5A0' : '#f87171',
                  fontWeight: 500,
                }}>
                  {testAlertResult.ok
                    ? `✓ Email envoyé à vos ${testAlertResult.count} bénéficiaire${testAlertResult.count > 1 ? 's' : ''}`
                    : '✗ Échec de l\'envoi — vérifiez la configuration email'}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Vault overview ── */}
        {total_vault_items > 0 && Object.keys(vault_by_category).length > 0 && (
          <div style={{ ...CARD, marginBottom: 28 }}>
            <h2 style={{ fontSize: 12, fontWeight: 600, margin: '0 0 16px', color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
              Aperçu du coffre-fort
            </h2>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {Object.entries(vault_by_category).map(([cat, count]) => (
                <div key={cat} onClick={() => navigate('/vault')}
                  style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 8, padding: '10px 18px', textAlign: 'center', cursor: 'pointer', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <div style={{ fontSize: 20, fontWeight: 700 }}>{count}</div>
                  <div style={{ fontSize: 12, color: G, marginTop: 2 }}>{categoryLabels[cat] || cat}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Transmission recap ── */}
        {beneficiariesLoaded && (
          <>
            <TransmissionWarnings
              beneficiaries={beneficiaries}
              overview={transmissionOverview}
              onNavigate={navigate}
            />
            <TransmissionRecap beneficiaries={beneficiaries} vaultLocked={!masterKey} />
          </>
        )}

        {/* ── Empty state ── */}
        {total_vault_items === 0 && total_beneficiaries === 0 && !has_ever_checked_in && (
          <div style={{ background: 'rgba(0,245,160,0.03)', border: '1px dashed rgba(0,245,160,0.2)', borderRadius: 16, padding: '32px 24px', textAlign: 'center' }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 8px' }}>Votre protection n'est pas encore configurée</h3>
            <p style={{ color: G, fontSize: 14, margin: '0 0 20px', maxWidth: 420, marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.6 }}>
              Pour que SafeHeir vous protège, commencez par ajouter un bénéficiaire, puis sécurisez vos premiers accès dans le coffre-fort.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button onClick={() => navigate('/beneficiaries')}
                style={{ background: S, color: '#0A1628', border: 'none', borderRadius: 8, padding: '10px 20px', fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
                Ajouter un bénéficiaire
              </button>
              <button onClick={() => navigate('/vault')}
                style={{ background: 'rgba(255,255,255,0.06)', color: '#fff', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '10px 20px', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>
                Ouvrir le coffre-fort
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const StatCard = ({ icon, label, value, sub, onClick, delay = '0s' }) => (
  <GlowCard float delay={delay} onClick={onClick}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
      <GlowIcon size={36} delay={delay}>{icon}</GlowIcon>
      <span style={{ fontSize: 13, color: G, fontWeight: 500 }}>{label}</span>
    </div>
    <div style={{ fontSize: 28, fontWeight: 700, color: '#fff', lineHeight: 1 }}>{value}</div>
    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)', marginTop: 4 }}>{sub}</div>
  </GlowCard>
);

const ActionButton = ({ label, sub, urgent, onClick }) => (
  <button onClick={onClick}
    style={{ background: urgent ? 'rgba(239,68,68,0.08)' : 'rgba(255,255,255,0.04)', border: urgent ? '1px solid rgba(239,68,68,0.25)' : '1px solid rgba(255,255,255,0.07)', borderRadius: 10, padding: '14px', textAlign: 'left', cursor: 'pointer', width: '100%' }}
    onMouseEnter={e => { if (!urgent) e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
    onMouseLeave={e => { if (!urgent) e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}>
    <div style={{ fontWeight: 600, fontSize: 14, color: urgent ? '#fca5a5' : '#fff', marginBottom: 3 }}>{label}</div>
    <div style={{ fontSize: 12, color: urgent ? '#f87171' : G }}>{sub}</div>
  </button>
);

export default Dashboard;
